import { zodResolver } from "@hookform/resolvers/zod";
import {
  AriaRadioGroup,
  KdsBadge,
  KdsButton,
  KdsCardSelectable,
  KdsModal,
  KdsText,
  KdsTextArea,
} from "@kazaar-platform/design-system";
import { RiCheckFill, RiCloseLine } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import dayjs from "dayjs";
import { size, uniqueId } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { type OrderItemDetailsSandboxItemSandboxItemQuoteReadDto } from "@/config/apiClient";
import { useToasts } from "@/hooks";
import { useOrderItemQuotes } from "@/hooks/queries/useOrderItemQuotes";
import { QuoteDisplay } from "./_partials/QuoteDisplay/QuoteDisplay";

import { type CardActionQuoteType } from "./QuoteValidationActions.types";
import {
  defaultValuesRefuseQuote,
  type InferredRefuseQuoteSchema,
  refuseQuoteSchema,
} from "./QuoteValidationActionsRefuseQuoteSchema";

export const QuoteValidationActions = ({
  orderId,
  orderItemId,
  quotes,
  onError,
}: CardActionQuoteType) => {
  // State
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
  const [isValidatingModalOpen, setIsValidatingModalOpen] =
    useState<boolean>(false);
  const [selectedQuote, setSelectedQuote] = useState<
    undefined | OrderItemDetailsSandboxItemSandboxItemQuoteReadDto
  >(undefined);

  const { t } = useTranslate();
  const { add } = useToasts();

  // Form refuse quotes
  const methods = useForm<InferredRefuseQuoteSchema>({
    defaultValues: defaultValuesRefuseQuote,
    resolver: zodResolver(refuseQuoteSchema),
  });
  const hasError = Boolean(methods.formState.errors.refusalReason);

  // Hooks
  const {
    useRefuseOrderItemsQuotes,
    useValidateOrderItemsQuote: useAcceptOrderItemsQuote,
  } = useOrderItemQuotes();
  const { mutateAsync: acceptSandboxItemQuote, isPending } =
    useAcceptOrderItemsQuote();
  const {
    mutateAsync: refuseSandboxItemQuotes,
    isPending: isRefuseSandboxItemQuotesPending,
  } = useRefuseOrderItemsQuotes();

  // Handle onValidate
  const handleValidateQuote = useCallback(async () => {
    if (selectedQuote) {
      const res = await acceptSandboxItemQuote({
        sandboxItemQuoteId: selectedQuote.id,
        orderId: orderId,
        orderItemId: orderItemId,
      });

      if (res.status === 201 || res.status === 200) {
        // Display success toast
        setSelectedQuote(undefined);
        add({
          state: "success",
          title: t(
            "order.myOrdersItemDetails.cardAction.quoteAvailabe.toast.success"
          ),
        });

        setIsValidatingModalOpen(false);
      } else {
        // Show Callout
        if (onError) {
          onError();
        }

        // Display error toast
        add({
          state: "error",
          title: t(
            "order.myOrdersItemDetails.cardAction.quoteAvailabe.toast.error"
          ),
        });
      }
    }
  }, [
    selectedQuote,
    acceptSandboxItemQuote,
    orderId,
    orderItemId,
    onError,
    add,
    t,
  ]);

  const handleRefuseQuote = useCallback(async () => {
    const refusalReason = methods.getValues("refusalReason");
    const result = await refuseSandboxItemQuotes({
      command: {
        commands: quotes.map((quote) => ({
          sandboxItemQuoteId: quote.id,
          refusalReason,
        })),
      },
      orderItemId: orderItemId,
      orderId: orderId,
    });

    if (result.status === 200 || result.status === 201) {
      setIsCancelModalOpen(false);

      // Display success toast
      add({
        state: "success",
        title: t(
          "order.myOrdersItemDetails.cardAction.quoteAvailabe.refuseToast.success"
        ),
      });
    } else {
      // Show Callout
      if (onError) {
        onError();
      }

      // Display error toast
      add({
        state: "error",
        title: t(
          "order.myOrdersItemDetails.cardAction.quoteAvailabe.refuseToast.error"
        ),
      });
    }
  }, [
    methods,
    refuseSandboxItemQuotes,
    quotes,
    orderItemId,
    orderId,
    onError,
    add,
    t,
  ]);

  // Sort Quotes
  const sortedQuotes = useMemo(
    () =>
      quotes.sort(
        (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix()
      ),
    [quotes]
  );

  // Get the product related to the quote depending on the current quote selected
  const productRelatedToQuote = useMemo<string[]>(() => {
    if (!selectedQuote) {
      return [];
    }

    return selectedQuote.SandboxItem?.map((si) => si.name as string) ?? [];
  }, [selectedQuote]);

  // Set First Quote as Selected
  useEffect(() => {
    if (quotes.length > 0) {
      setSelectedQuote(quotes[0]);
    }
  }, [quotes]);

  return (
    <>
      <div className="flex flex-col gap-sp-md rounded-2xl bg-white !p-0">
        {sortedQuotes.length > 0 && (
          <AriaRadioGroup
            aria-label={t("cardQuote.selectQuote")}
            onChange={(id: string) => {
              const quote = sortedQuotes.find((q) => q.id === id);
              setSelectedQuote(quote);
            }}
            className="flex flex-col gap-sp-sm"
            value={selectedQuote?.id}
          >
            {sortedQuotes.map((quote) => {
              const relatedItemNames =
                quote.SandboxItem?.map((si) => si.name ?? "") ?? [];

              return (
                <div key={quote.id} className="relative">
                  <KdsCardSelectable
                    direction="horizontal"
                    value={quote.id}
                    content={
                      <QuoteDisplay
                        itemsRelated={relatedItemNames}
                        quote={quote}
                      />
                    }
                    onChange={() => setSelectedQuote(quote)}
                  />
                </div>
              );
            })}
          </AriaRadioGroup>
        )}
        <div className="flex w-full items-center justify-end gap-sp-xs">
          <KdsButton
            variant="outlined"
            color="error"
            iconLeft={RiCloseLine}
            size="small"
            onPress={() => setIsCancelModalOpen(true)}
          >
            {t("buttons.refuse")}
          </KdsButton>

          <KdsButton
            color="success"
            size="small"
            iconLeft={RiCheckFill}
            dataCy="openModalPreValidationQuote"
            onPress={() => setIsValidatingModalOpen(true)}
          >
            {t("buttons.validateAction")}
          </KdsButton>
        </div>
      </div>

      <KdsModal
        isOpen={isCancelModalOpen}
        onOpenChange={setIsCancelModalOpen}
        size="md"
        dialogProps={{
          dialogHeaderProps: {
            title: t(
              "order.myOrdersItemDetails.cardAction.quoteAvailabe.cancelButton",
              {
                count: quotes.length,
              }
            ),
          },
          content: (
            <KdsTextArea
              {...methods.register("refusalReason", { required: true })}
              hasError={hasError}
              errorMessage={t(
                "order.myOrdersItemDetails.cardAction.quoteAvailabe.errorRefusalReason"
              )}
            />
          ),
          dialogFooterProps: {
            type: "double",
            buttonProps: [
              {
                children: t("buttons.cancel"),
                variant: "ghost",
                color: "neutral",
                isLoading: isPending || isRefuseSandboxItemQuotesPending,
                disabled: isPending || isRefuseSandboxItemQuotesPending,
                onPress: () => {
                  methods.reset();
                  setIsCancelModalOpen(false);
                },
              },
              {
                children: t("buttons.send"),
                type: "submit",
                isLoading: isPending || isRefuseSandboxItemQuotesPending,
                disabled: isPending || isRefuseSandboxItemQuotesPending,
                onPress: () => void methods.handleSubmit(handleRefuseQuote)(),
              },
            ],
          },
        }}
        callbackOnClose={methods.reset}
      />

      <KdsModal
        isOpen={isValidatingModalOpen}
        onOpenChange={setIsValidatingModalOpen}
        size="md"
        dialogProps={{
          dialogHeaderProps: {
            title: t(
              "order.myOrdersItemDetails.cardAction.quoteAvailabe.modalConfirmValidation.title"
            ),
          },
          content: (
            <div className="flex flex-col gap-sp-md">
              <KdsText size="body-md">
                {t(
                  "order.myOrdersItemDetails.cardAction.quoteAvailabe.modalConfirmValidation.description",
                  {
                    count: size(productRelatedToQuote),
                    numberProduct: size(productRelatedToQuote),
                    quoteName: selectedQuote?.quoteNumber,
                  }
                )}
              </KdsText>

              <div className="flex w-full flex-wrap items-center gap-2">
                {productRelatedToQuote.map((item) => (
                  <KdsBadge key={uniqueId()} state="warning" size="sm">
                    {item}
                  </KdsBadge>
                ))}
              </div>
            </div>
          ),
          dialogFooterProps: {
            type: "double",
            buttonProps: [
              {
                children: t("buttons.cancel"),
                variant: "ghost",
                color: "neutral",
                onPress: () => setIsValidatingModalOpen(false),
              },
              {
                children: t("buttons.validate"),
                type: "submit",
                isLoading: isPending,
                disabled: !selectedQuote || isPending,
                dataCy: "validateQuoteButton",
                onPress: () => void handleValidateQuote(),
              },
            ],
          },
        }}
      />
    </>
  );
};
